import { useMemo } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';

// project-imports
import IconButton from 'components/@extended/IconButton';
import DrawerHeader from './DrawerHeader';
import DrawerContent from './DrawerContent';
import MiniDrawerStyled from './MiniDrawerStyled';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config';

// assets
import { ArrowLeft2, ArrowRight2 } from 'iconsax-reactjs';

interface Props {
  window?: () => Window;
}

// ==============================|| MAIN LAYOUT - DRAWER ||============================== //

export default function MainDrawer({ window }: Props) {
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  // responsive drawer container
  const container = window !== undefined ? () => window().document.body : undefined;

  // header content
  const drawerContent = useMemo(() => <DrawerContent />, []);
  const drawerHeader = useMemo(() => <DrawerHeader open={drawerOpen} />, [drawerOpen]);

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { md: 0 },
        position: 'relative',
        width: { xs: 0, lg: drawerOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH },
        zIndex: 1200
      }}
      aria-label="mailbox folders"
    >
      {!downLG ? (
        <>
          <MiniDrawerStyled variant="permanent" open={drawerOpen}>
            {drawerHeader}
            {drawerContent}
          </MiniDrawerStyled>
          <IconButton
            aria-label={drawerOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            color="secondary"
            shape="rounded"
            onClick={() => handlerDrawerOpen(!drawerOpen)}
            sx={(theme) => ({
              position: 'absolute',
              top: 20,
              right: -16,
              zIndex: theme.zIndex.drawer + 2,
              width: 32,
              height: 32,
              p: 0,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: theme.vars.customShadows.z1,
              color: 'secondary.main',
              '&:hover': {
                bgcolor: 'background.paper',
                color: 'primary.main'
              }
            })}
          >
            {drawerOpen ? <ArrowLeft2 size={18} /> : <ArrowRight2 size={18} />}
          </IconButton>
        </>
      ) : (
        <Drawer
          container={container}
          variant="temporary"
          open={drawerOpen}
          onClose={() => handlerDrawerOpen(!drawerOpen)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: drawerOpen ? 'block' : 'none', lg: 'none' } }}
          slotProps={{
            paper: {
              sx: {
                boxSizing: 'border-box',
                width: DRAWER_WIDTH,
                borderRight: '1px solid',
                borderColor: 'divider',
                backgroundImage: 'none',
                boxShadow: 'inherit'
              }
            }
          }}
        >
          {drawerHeader}
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
}
